import axios from 'axios';
import { AuthTokens } from '../types/auth';
// API URLの設定 - 環境変数から取得、未設定時はHeroku環境向けの値を使用
const API_URL = import.meta.env.VITE_API_URL || `${window.location.origin}/api`;
// APIクライアントの作成
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// リクエストインターセプター
apiClient.interceptors.request.use(
  (config) => {
    // ローカルストレージからトークンを取得
    const tokensStr = localStorage.getItem('tokens');
    if (tokensStr) {
      try {
        const tokens: AuthTokens = JSON.parse(tokensStr);
        if (tokens.access && config.headers) {
          config.headers.Authorization = `Bearer ${tokens.access}`;
        }
      } catch (error) {
        // トークンのパース失敗
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// バージョン情報用のローカルストレージキー
const VERSION_KEY = 'app_version';

// バージョン変更検出用ログ（現在は無効化済み）
const logVersion = (message: string): void => {
  // ログ出力は無効化
};

// バージョン情報を比較して変更があればリロード
const checkVersionAndReload = (newVersion: string): void => {
  if (!newVersion) return;
  
  try {
    // 既存のバージョン情報を取得
    const storedVersion = localStorage.getItem(VERSION_KEY);
    let currentVersion = '';
    
    if (storedVersion) {
      try {
        const parsed = JSON.parse(storedVersion);
        currentVersion = parsed.version;
      } catch (e) {
        // エラー時は何もしない
      }
    }
    
    // 保存されたバージョンが存在し、新しいバージョンと異なる場合
    if (currentVersion && 
        currentVersion !== newVersion && 
        currentVersion !== 'unknown' && 
        newVersion !== 'unknown') {
      
      // 更新検出フラグをセット
      // このフラグはリロード後に通知を表示するために使用
      sessionStorage.setItem('app_updated', JSON.stringify({
        version: newVersion,
        from: currentVersion,
        timestamp: new Date().toISOString()
      }));
      
      // 新しいバージョン情報を保存
      localStorage.setItem(VERSION_KEY, JSON.stringify({
        version: newVersion,
        releaseDate: new Date().toISOString()
      }));
      
      // ユーザーに通知せず、自動的にリロード
      // 現在のURLに強制的にリダイレクト（キャッシュを回避する効果がある）
      window.location.href = window.location.href;
      
    } else if (!currentVersion) {
      // 初回アクセス時は情報を保存
      localStorage.setItem(VERSION_KEY, JSON.stringify({
        version: newVersion,
        releaseDate: new Date().toISOString()
      }));
    } else {
      // 変更がない場合は何もしない
    }
  } catch (e) {
    // エラー時は何もしない
  }
};

// レスポンスインターセプター
apiClient.interceptors.response.use(
  (response) => {
    // APIバージョン情報をヘッダーから取得
    const apiVersion = 
      response.headers['x-api-version'] || 
      response.headers['X-API-Version'];
    
    // バージョンをチェックしてリロードが必要か判断
    if (apiVersion) {
      checkVersionAndReload(apiVersion);
    }
    
    return response;
  },
  async (error) => {
    // レスポンスからバージョン情報を取得 (エラー時でもヘッダーがある場合)
    if (error.response && error.response.headers) {
      const apiVersion = 
        error.response.headers['x-api-version'] || 
        error.response.headers['X-API-Version'];
      
      if (apiVersion) {
        checkVersionAndReload(apiVersion);
      }
    }
    
    const originalRequest = error.config;
    
    // 重要: リクエストにリトライフラグがない場合だけ進む
    if (!originalRequest || originalRequest._isRetry) {
      return Promise.reject(error);
    }
    
    // ユーザーが無効か削除されている場合（403）
    if (error.response?.status === 403) {
      // アカウントが無効または削除されている場合
      
      // ログイン処理のリクエストの場合は、単にエラーを返す (リダイレクトしない)
      if (originalRequest.url?.includes('/auth/login/')) {
        return Promise.reject(error);
      }
      
      // それ以外の場合は、クリーンアップしてログイン画面にリダイレクト
      localStorage.removeItem('tokens');
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
      return Promise.reject(error);
    }

    // トークンの有効期限切れの場合（401）かつリトライしていない場合のみ
    if (error.response?.status === 401 && !originalRequest._isRetry) {
      // リトライフラグを設定
      originalRequest._isRetry = true;

      try {
        // リフレッシュトークンを使用して新しいアクセストークンを取得
        const tokensStr = localStorage.getItem('tokens');
        if (tokensStr) {
          const tokens: AuthTokens = JSON.parse(tokensStr);
          if (tokens.refresh) {
            const response = await axios.post<AuthTokens>(`${API_URL}/auth/refresh/`, {
              refresh: tokens.refresh
            });
            
            // 新しいトークンを保存
            localStorage.setItem('tokens', JSON.stringify(response.data));
            
            // 新しいトークンでリクエストを再試行
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
            }
            return apiClient(originalRequest);
          }
        }
        throw new Error('リフレッシュトークンがありません');
      } catch (refreshError) {
        // リフレッシュトークンも無効な場合はログアウト
        localStorage.removeItem('tokens');
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * バージョン変更テスト用関数
 * ブラウザコンソールから呼び出すことでバージョン変更をシミュレート
 * 例: window.testVersionChange('1.0.2')
 */
export const testVersionChange = (newVersion: string): void => {
  // 既存のバージョン情報を取得
  const storedVersion = localStorage.getItem(VERSION_KEY);
  let currentInfo = {
    version: 'unknown',
    releaseDate: new Date().toISOString()
  };
  
  if (storedVersion) {
    try {
      currentInfo = JSON.parse(storedVersion);
    } catch (e) {
      // エラー時は何もしない
    }
  }
  
  // 更新検出フラグをセット
  sessionStorage.setItem('app_updated', JSON.stringify({
    version: newVersion,
    from: currentInfo.version,
    timestamp: new Date().toISOString()
  }));
  
  // バージョン情報を更新
  currentInfo.version = newVersion;
  localStorage.setItem(VERSION_KEY, JSON.stringify(currentInfo));
  
  // リダイレクトして更新を通知
  window.location.href = window.location.href;
};

// グローバルに関数を追加（ブラウザコンソールからアクセス可能にする）
(window as any).testVersionChange = testVersionChange;

export default apiClient;